/**
 * /settings/bank-connections — connect a bank via Plaid Link, map accounts to
 * Ledger Core bank accounts, and sync transactions into the register.
 *
 * The Plaid *app* is a Bynaus platform key (set by an admin in Settings → API
 * Keys); each bank login here is the customer's own connection.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePlaidLink } from "react-plaid-link";

import { api } from "@/lib/api";
import { bankAccountsListQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/settings/bank-connections")({
  component: BankConnectionsPage,
});

interface PlaidAccount {
  id: string; plaid_account_id: string; name: string | null; mask: string | null;
  type: string | null; subtype: string | null; bank_account_id: string | null;
}
interface PlaidItem { id: string; institution_name: string | null; status: string; accounts: PlaidAccount[] }
interface BankAccount { id: string; name: string }

function BankConnectionsPage() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["plaid-status"], queryFn: () => api<{ configured: boolean; environment: string }>("/api/plaid/status") });
  const { data: items } = useQuery({ queryKey: ["plaid-items"], queryFn: () => api<PlaidItem[]>("/api/plaid/items") });
  const { data: banks } = useQuery(bankAccountsListQueryOptions());

  const refresh = () => qc.invalidateQueries({ queryKey: ["plaid-items"] });

  return (
    <div className="h-full overflow-y-auto bg-slate-50 dark:bg-gray-950 px-6 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Bank connections</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Connect your bank to pull transactions automatically.
            </p>
          </div>
          {status?.configured && <ConnectBank onConnected={refresh} />}
        </div>

        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100">
          <p className="font-semibold">Event-driven accounting—not an overnight batch</p>
          <p className="mt-1 text-blue-800 dark:text-blue-200">
            As Plaid transactions become available, Ledger Core brings them into reconciliation,
            cash visibility, and continuous-close controls on the same accounting spine. Plaid is
            read-only and cannot move money. Delivery timing depends on each institution, so a feed
            may be near-real-time rather than instantaneous; the bank statement remains the final tie-out.
          </p>
        </div>

        {status && !status.configured && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 p-4 text-sm text-amber-800 dark:text-amber-200">
            Bank connections aren't enabled yet. A Bynaus administrator needs to add the Plaid keys in
            <span className="font-medium"> Settings → API Keys</span>.
          </div>
        )}

        {status?.configured && (!items || items.length === 0) && (
          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-sm text-slate-500 dark:text-slate-400">
            No banks connected yet. Click <span className="font-medium">Connect a bank</span> to link one.
          </div>
        )}

        {(items ?? []).map((item) => (
          <ItemCard key={item.id} item={item} banks={banks ?? []} onChanged={refresh} />
        ))}
      </div>
    </div>
  );
}

function ConnectBank({ onConnected }: { onConnected: () => void }) {
  // OAuth banks redirect the browser back here with ?oauth_state_id=… . To
  // resume Link we must re-init with the SAME link_token (stashed in
  // localStorage before we opened) plus receivedRedirectUri.
  const isOAuthReturn = typeof window !== "undefined"
    && window.location.search.includes("oauth_state_id");
  const [token, setToken] = useState<string | null>(
    isOAuthReturn ? localStorage.getItem("plaid_link_token") : null,
  );
  const [busy, setBusy] = useState(isOAuthReturn);

  const finish = () => {
    localStorage.removeItem("plaid_link_token");
    setToken(null);
    setBusy(false);
    // Strip the OAuth params so a refresh doesn't re-trigger the resume.
    if (isOAuthReturn) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  const { open, ready } = usePlaidLink({
    token: token ?? "",
    receivedRedirectUri: isOAuthReturn ? window.location.href : undefined,
    onSuccess: async (public_token) => {
      try { await api("/api/plaid/exchange", { method: "POST", body: { public_token } }); }
      finally { finish(); onConnected(); }
    },
    onExit: () => { finish(); },
  });

  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api<{ link_token: string }>("/api/plaid/link-token", { method: "POST", body: {} });
      localStorage.setItem("plaid_link_token", r.link_token);  // survive OAuth redirect
      setToken(r.link_token);
    } catch { setBusy(false); }
  };

  return (
    <button
      type="button" onClick={start} disabled={busy}
      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
    >{busy ? "Connecting…" : "🏦 Connect a bank"}</button>
  );
}

function ItemCard({ item, banks, onChanged }: { item: PlaidItem; banks: BankAccount[]; onChanged: () => void }) {
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "sync" | "disconnect" | "setup">(null);

  const linkAccount = async (acct: PlaidAccount, bankId: string) => {
    if (!bankId) return;
    await api(`/api/plaid/accounts/${acct.id}/link`, { method: "POST", body: { bank_account_id: bankId } });
    onChanged();
  };
  const sync = async () => {
    setBusy("sync"); setSyncMsg(null);
    try {
      const r = await api<{ added: number; skipped: number; coded?: number; to_suspense?: number; note?: string }>(`/api/plaid/items/${item.id}/sync`, { method: "POST", body: {} });
      setSyncMsg(
        r.note
          ? r.note
          : `Added ${r.added}` +
            (r.added ? ` — ${r.coded ?? 0} auto-coded, ${r.to_suspense ?? 0} to review in Suspense` : "") +
            `${r.skipped ? `, skipped ${r.skipped}` : ""}.`,
      );
    } catch (e) { setSyncMsg((e as Error).message); }
    finally { setBusy(null); }
  };
  const setupAccounts = async () => {
    setBusy("setup"); setSyncMsg(null);
    try {
      const r = await api<{ count: number }>(`/api/plaid/items/${item.id}/provision-accounts`, { method: "POST", body: {} });
      setSyncMsg(r.count > 0 ? `Created ${r.count} ledger bank account${r.count > 1 ? "s" : ""}.` : "All accounts already set up.");
      onChanged();
    } catch (e) { setSyncMsg((e as Error).message); }
    finally { setBusy(null); }
  };
  // Only depository (cash) accounts are feed-eligible — credit cards,
  // investments, and loans are never mapped, so they must not keep the
  // "Set up accounts" button alive forever.
  const isCash = (a: PlaidAccount) => (a.type ?? "").toLowerCase() === "depository";
  const hasUnlinked = item.accounts.some((a) => !a.bank_account_id && isCash(a));
  const disconnect = async () => {
    setBusy("disconnect");
    try { await api(`/api/plaid/items/${item.id}`, { method: "DELETE" }); onChanged(); }
    finally { setBusy(null); }
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.institution_name ?? "Bank"}</span>
          <span className="rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 px-2 py-0.5 text-[11px]">{item.status}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasUnlinked && (
            <button type="button" onClick={setupAccounts} disabled={busy !== null}
              title="Auto-create a ledger bank account for each connected account"
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy === "setup" ? "Setting up…" : "Set up accounts"}</button>
          )}
          <button type="button" onClick={sync} disabled={busy !== null}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy === "sync" ? "Syncing…" : "Sync transactions"}</button>
          <button type="button" onClick={disconnect} disabled={busy !== null}
            className="rounded-md border border-slate-200 dark:border-gray-700 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50">
            Disconnect</button>
        </div>
      </div>
      {syncMsg && (
        <p className="mx-4 mt-2 rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          {syncMsg}
        </p>
      )}
      <div className="divide-y divide-slate-100 dark:divide-gray-800">
        {item.accounts.map((a) => (
          <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-slate-800 dark:text-slate-200">
                {a.name ?? "Account"}{a.mask ? ` ···${a.mask}` : ""}
              </div>
              <div className="text-xs text-slate-400">{[a.type, a.subtype].filter(Boolean).join(" · ")}</div>
            </div>
            {isCash(a) ? (
              <select
                value={a.bank_account_id ?? ""}
                onChange={(e) => linkAccount(a, e.target.value)}
                className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
              >
                <option value="">— map to bank account —</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            ) : (
              <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500 italic">
                Not a cash account — no transaction feed
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
